import { useMutation } from "@tanstack/react-query";
import { signInWithPopup, signOut } from "firebase/auth";
import { ArrowRight, LockKeyhole } from "lucide-react";
import { FormEvent, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Brand } from "../../components/Brand";
import { loginWithGoogle, loginWithPassword } from "../../lib/api";
import { auth, googleProvider } from "../../lib/firebase";

export function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const googleMutation = useMutation({
    mutationFn: async () => {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      try {
        return await loginWithGoogle(idToken);
      } catch (error) {
        await signOut(auth);
        throw error;
      }
    },
    onSuccess: () => navigate("/admin", { replace: true }),
  });
  const passwordMutation = useMutation({
    mutationFn: () => loginWithPassword(username, password),
    onSuccess: () => {
      setPassword("");
      navigate("/admin", { replace: true });
    },
  });

  const submitPassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (username.trim() && password) passwordMutation.mutate();
  };

  if (localStorage.getItem("steria_admin_token")) return <Navigate to="/admin" replace />;

  return (
    <main className="login-page">
      <section className="login-story">
        <Brand />
        <div>
          <span className="login-kicker">STERIA HOUSE CMS</span>
          <h1>Shape the house from one calm workspace.</h1>
          <p>Manage every story, product, image, and idea your public website presents.</p>
        </div>
        <small>Private administration</small>
      </section>
      <section className="login-panel">
        <div className="login-google-card">
          <span className="login-lock"><LockKeyhole size={22} /></span>
          <h2>Welcome back</h2>
          <p>Sign in to manage Steria House.</p>
          {googleMutation.isError && (
            <div className="login-error">Google sign-in is unavailable. Use your administrator credentials instead.</div>
          )}
          {passwordMutation.isError && (
            <div className="login-error">Invalid credentials. Please try again.</div>
          )}
          <button className="button button-primary login-submit" disabled={googleMutation.isPending || passwordMutation.isPending} type="button" onClick={() => googleMutation.mutate()}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-3.53 6.16-4.53z" />
            </svg>
            {googleMutation.isPending ? "Connecting..." : "Continue with Google"} <ArrowRight size={17} />
          </button>
          <div className="login-divider"><span>or</span></div>
          <form onSubmit={submitPassword}>
            <label>
              Username
              <input
                autoComplete="username"
                disabled={googleMutation.isPending || passwordMutation.isPending}
                onChange={(event) => setUsername(event.target.value)}
                required
                value={username}
              />
            </label>
            <label>
              Password
              <input
                autoComplete="current-password"
                disabled={googleMutation.isPending || passwordMutation.isPending}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
            </label>
            <button className="button button-secondary login-submit" disabled={googleMutation.isPending || passwordMutation.isPending} type="submit">
              {passwordMutation.isPending ? "Signing in..." : "Continue with password"} <ArrowRight size={17} />
            </button>
          </form>
          <small className="login-demo">Use the credentials provided by your administrator.</small>
        </div>
      </section>
    </main>
  );
}
